import { Dec, dec, toDecimalString } from './decimal.js';
const UNICODE_FRACTIONS = {
    '¼': '1/4',
    '½': '1/2',
    '¾': '3/4',
    '⅓': '1/3',
    '⅔': '2/3',
    '⅛': '1/8',
    '⅜': '3/8',
    '⅝': '5/8',
    '⅞': '7/8',
    '⅕': '1/5',
    '⅖': '2/5',
    '⅗': '3/5',
    '⅘': '4/5',
    '⅙': '1/6',
    '⅚': '5/6',
};
const FRACTION_GLYPHS = [
    { num: 1, den: 2, glyph: '½' },
    { num: 1, den: 3, glyph: '⅓' },
    { num: 2, den: 3, glyph: '⅔' },
    { num: 1, den: 4, glyph: '¼' },
    { num: 3, den: 4, glyph: '¾' },
    { num: 1, den: 8, glyph: '⅛' },
    { num: 3, den: 8, glyph: '⅜' },
    { num: 5, den: 8, glyph: '⅝' },
    { num: 7, den: 8, glyph: '⅞' },
];
/** Replace unicode vulgar fractions with ASCII "n/d" forms (adding a space after a leading integer). */
export function expandUnicodeFractions(text) {
    return text.replace(/(\d)?([¼½¾⅓⅔⅛⅜⅝⅞⅕⅖⅗⅘⅙⅚])/g, (_m, digit, glyph) => {
        const frac = UNICODE_FRACTIONS[glyph] ?? glyph;
        return digit ? `${digit} ${frac}` : frac;
    });
}
const NUMBER_TOKEN = String.raw `(?:\d+(?:[.,]\d+)?)`;
const FRACTION_TOKEN = String.raw `(?:\d+\s*/\s*\d+)`;
const MIXED_TOKEN = String.raw `(?:\d+\s+\d+\s*/\s*\d+)`;
export const QUANTITY_TOKEN = String.raw `(?:${MIXED_TOKEN}|${FRACTION_TOKEN}|${NUMBER_TOKEN})`;
/**
 * Parse a single quantity token: "1", "1.5", "1,5", "1/2", "1 1/2", "½", "1½".
 * Returns null when the token is not a quantity.
 */
export function parseQuantity(raw) {
    const text = expandUnicodeFractions(raw.trim()).replace(/\s+/g, ' ');
    if (!text)
        return null;
    const mixed = text.match(/^(\d+) (\d+)\s*\/\s*(\d+)$/);
    if (mixed) {
        const [, whole, num, den] = mixed;
        if (Number(den) === 0)
            return null;
        return toDecimalString(dec(whole).plus(dec(num).div(dec(den))));
    }
    const frac = text.match(/^(\d+)\s*\/\s*(\d+)$/);
    if (frac) {
        const [, num, den] = frac;
        if (Number(den) === 0)
            return null;
        return toDecimalString(dec(num).div(dec(den)));
    }
    const num = text.match(/^(\d+)(?:[.,](\d+))?$/);
    if (num) {
        const [, whole, decimals] = num;
        return toDecimalString(decimals ? `${whole}.${decimals}` : whole);
    }
    return null;
}
/**
 * Split a decimal into whole part and (if close enough) a common fraction glyph.
 * `tolerance` is the maximum absolute error accepted when snapping to a glyph.
 */
export function toFractionParts(value, tolerance = '0.02') {
    const d = dec(value);
    const negative = d.isNegative();
    const abs = d.abs();
    const whole = abs.floor();
    const rem = abs.minus(whole);
    const tol = dec(tolerance);
    if (rem.isZero()) {
        return { whole: whole.toFixed(0), glyph: null, remainder: null, negative };
    }
    let best = null;
    for (const f of FRACTION_GLYPHS) {
        const err = rem.minus(dec(f.num).div(f.den)).abs();
        if (err.lte(tol) && (!best || err.lt(best.err)))
            best = { glyph: f.glyph, err };
    }
    if (best) {
        return { whole: whole.toFixed(0), glyph: best.glyph, remainder: null, negative };
    }
    // Snap 0.98.. up to the next integer within tolerance.
    if (dec(1).minus(rem).lte(tol)) {
        return { whole: whole.plus(1).toFixed(0), glyph: null, remainder: null, negative };
    }
    return { whole: whole.toFixed(0), glyph: null, remainder: toDecimalString(rem), negative };
}
/**
 * Human readable quantity: "½", "1 ½", "2", "1.25" (or "1,25" for de).
 * Values that cannot be expressed with a glyph fall back to a decimal with at most
 * `maxDecimals` fraction digits.
 */
export function formatFraction(value, opts = {}) {
    const { locale = 'en', maxDecimals = 2, preferFractions = true } = opts;
    const d = dec(value);
    const parts = preferFractions ? toFractionParts(d) : null;
    const sign = d.isNegative() ? '-' : '';
    if (parts && parts.remainder === null) {
        if (parts.glyph) {
            return sign + (parts.whole === '0' ? parts.glyph : `${parts.whole} ${parts.glyph}`);
        }
        return sign + parts.whole;
    }
    const rounded = d.abs().toDecimalPlaces(maxDecimals, Dec.ROUND_HALF_UP);
    let text = rounded.toFixed(maxDecimals).replace(/\.?0+$/, '');
    if (text === '')
        text = '0';
    if (locale === 'de')
        text = text.replace('.', ',');
    return sign + text;
}
//# sourceMappingURL=fraction.js.map